import type { ClosingOffer } from './public-chat-turns'

/**
 * La sesión del chat público, aparte de la conversación (que es del reducer
 * compartido): si empezó en esta visita, cómo acabó, cuántos mensajes quedan
 * y en qué va la apertura. Un solo reducer: cada cambio es una acción.
 */
export interface PublicSession {
  started: boolean
  closing: ClosingOffer | null
  /** Lo que dijo el servidor; `null` hasta abrir (o en una sesión reanudada, que no lo sabe). */
  quota: { left: number; total: number | null } | null
  open: { status: 'idle' } | { status: 'busy' } | { status: 'failed'; error: string }
}

export type PublicSessionAction =
  | { type: 'openStarted' }
  | { type: 'openFailed'; error: string }
  /** La apertura llegó: con sesión (`quota`) o con el cierre de hoy (`closing`). */
  | { type: 'opened'; quota: PublicSession['quota']; closing: ClosingOffer | null }
  | { type: 'turnCounted'; left: number }
  | { type: 'closed'; offer: ClosingOffer }
  /** El token ya no vale: la puerta vuelve a decidir. */
  | { type: 'forgotten' }

export const INITIAL_PUBLIC_SESSION: PublicSession = { started: false, closing: null, quota: null, open: { status: 'idle' } }

export function publicSessionReducer(session: PublicSession, action: PublicSessionAction): PublicSession {
  switch (action.type) {
    case 'openStarted':
      return { ...session, open: { status: 'busy' } }
    case 'openFailed':
      return { ...session, open: { status: 'failed', error: action.error } }
    case 'opened':
      return { ...session, started: true, quota: action.quota, closing: action.closing, open: { status: 'idle' } }
    case 'turnCounted':
      return { ...session, quota: { left: action.left, total: session.quota?.total ?? null } }
    case 'closed':
      return { ...session, closing: action.offer, quota: { left: 0, total: session.quota?.total ?? null } }
    case 'forgotten':
      return { ...session, started: false, quota: null }
  }
}

/** El número del mensaje que se envía ahora, contado desde lo que dijo el servidor (`null` si no lo sabe). */
export function nextMessageNumber(session: PublicSession): number | null {
  const quota = session.quota
  if (!quota || quota.total === null) return null
  return quota.total - quota.left + 1
}
